import Image from 'next/image';
import React from 'react';

/**
 * SectionBenefits Component
 * Benefits section showing advantages of becoming an agent
 * Updated to match Figma design with 7 benefit cards
 */
const SectionBenefits: React.FC = () => {
  // Benefit images
  const bonusImage = "/assets/images/benefit-new/training.webp";
  const trainingImage = "/assets/images/benefit-new/bonus.webp";

  const umrohImage = "/assets/images/benefit-new/umroh.webp";
  const beatImage = "/assets/images/benefit-new/beat.webp";
  const smartphoneImage = "/assets/images/benefit-new/smartphone.webp";
  const laptopImage = "/assets/images/benefit-new/laptop.webp";
  const madpadImage = "/assets/images/benefit-new/madpad.webp";

  // Row 1: Benefits with descriptions
  const mainBenefits = [
    {
      title: 'Training Gratis',
      description: 'Pelatihan lengkap tentang produk, teknik penjualan, digital marketing, manajemen bisnis, komunikasi persuasi, dll.',
      image: trainingImage,
    },
    {
      title: 'Bonus & Insentif',
      description: 'Bonus menanti untuk setiap agent terbaik Pekalen Rafting, karena kami menghargai setiap kerja keras Anda.',
      image: bonusImage,
    }
  ];

  // Row 2: Benefits without descriptions (only titles)
  const additionalBenefits = [
    { title: 'Umroh', image: umrohImage },
    { title: 'Honda BeAT', image: smartphoneImage },
    { title: 'Smartphone', image: laptopImage },
    { title: 'Laptop', image:  beatImage},
    { title: 'Mad Pad', image: madpadImage }
  ];

  return (
    <div
      data-aos="fade-up"
      className="lg:py-[52px] md:py-8 py-6 lg:px-0 md:px-12 px-6 flex flex-col gap-16 items-center"
    >
      {/* Benefits Section */}
      <div className="w-full flex flex-col lg:gap-12 md:gap-10 gap-8 items-center max-w-6xl mx-auto">

        {/* Section Title */}
        <h2 className="font-bold lg:text-[40px] md:text-[32px] text-[24px] tracking-[0.4px] text-[#0a0a0a] text-center m-0 font-family-roboto-slab">
          Benefit Jadi Agen & Mitra Pekalen Rafting
        </h2>

        {/* Row 1: Main Benefits (Training & Bonus) with descriptions */}
        <div className="flex lg:gap-12 md:gap-8 gap-6 w-full justify-center flex-wrap">
          {mainBenefits.map((benefit, index) => (
            <div
              key={index}
              data-aos="fade-up"
              data-aos-delay={index * 150}
              className="flex-[1_0_0] min-w-0 flex flex-col lg:gap-6 md:gap-5 gap-4 items-center"
              style={{ maxWidth: 'calc(50% - 24px)' }}
            >
              {/* Image Container with Shadow Effect */}
              <div className="aspect-[352/202] relative w-full">
                {/* Shadow Layer */}
                <div
                  className="absolute aspect-[344/194] left-2 right-0 top-[calc(50%+4.25px)] translate-y-[-50%] bg-[#222222]"
                  style={{ zIndex: 0 }}
                />

                {/* Image Layer */}
                <div className="absolute aspect-[344/194] left-0 right-2 top-[calc(50%-3.75px)] translate-y-[-50%] overflow-hidden shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]" style={{ zIndex: 1 }}>
                  <Image
                    width={800}
                    height={600}
                    src={benefit.image}
                    alt={benefit.title}
                    className="w-full h-full object-cover"
                    quality={75}
                    sizes="(min-width: 1024px) 400px, (min-width: 768px) 300px, 100vw"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="flex flex-col lg:gap-3 md:gap-2.5 gap-2 text-center px-4">
                <p className="font-bold lg:text-[24px] md:text-[20px] text-[18px] tracking-[-0.32px] text-[#222222] m-0 font-family-open-sans">
                  {benefit.title}
                </p>
                <p className="font-normal lg:text-[16px] md:text-[14px] text-[13px] lg:leading-7 md:leading-6 leading-5 tracking-[0.16px] text-[#666666] m-0 font-family-open-sans">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Row 2: Additional Benefits (no descriptions) */}
        <div className="flex lg:gap-12 md:gap-8 gap-6 w-full justify-center flex-wrap">
          {additionalBenefits.map((benefit, index) => (
            <div
              key={index}
              data-aos="fade-up"
              data-aos-delay={(index + 2) * 150}
              className="flex-[1_0_0] min-w-0 flex flex-col lg:gap-6 md:gap-5 gap-4 items-center"
              style={{ maxWidth: 'calc(20% - 38.4px)', minWidth: '150px' }}
            >
              {/* Image Container with Shadow Effect */}
              <div className="aspect-[352/202] relative w-full">
                {/* Shadow Layer */}
                <div
                  className="absolute aspect-[344/194] left-2 right-[-0.4px] top-[calc(50%+3.56px)] translate-y-[-50%] bg-[#222222]"
                  style={{ zIndex: 0 }}
                />

                {/* Image Layer */}
                <div className="absolute aspect-[344/194] left-0 right-[7.6px] top-[calc(50%-4.44px)] translate-y-[-50%] overflow-hidden shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]" style={{ zIndex: 1 }}>
                  <Image
                    width={800}
                    height={600}
                    src={benefit.image}
                    alt={benefit.title}
                    className="w-full h-full object-cover"
                    quality={75}
                    sizes="(min-width: 1024px) 250px, (min-width: 768px) 200px, 150px"
                  />
                </div>
              </div>

              {/* Content - Title Only */}
              <div className="flex flex-col items-center px-4 w-full">
                <p className="font-bold lg:text-[24px] md:text-[20px] text-[18px] tracking-[-0.32px] text-[#222222] text-center m-0 font-family-open-sans">
                  {benefit.title}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SectionBenefits;
